import type { Chain } from '@/types'
import { CHAIN_REGISTRY, isChainOnThisBuild } from './chains'

export interface TokenPickerItem {
  symbol: string
  name: string
  isNative: boolean
  color?: string
  availableOn?: Chain[]
}

/**
 * A normalized token-picker selection. `symbol` is the CANONICAL token used everywhere
 * downstream (dexRoutes match, balance lookup, quote/bridge request); `chain`, when set,
 * pins the exact source/dest chain the selection implies. For the network-first native
 * list a row carries both; for stablecoins `chain` is omitted (chain stays / follows the
 * existing `availableOn` rule).
 */
export interface TokenSelection {
  symbol: string
  chain?: Chain
}

export const TOKEN_PICKER_LIST: TokenPickerItem[] = [
  { symbol: 'USDC', name: 'USD Coin', isNative: false },
  { symbol: 'USDT', name: 'Tether',   isNative: false },
  // #174 — USDG (Global Dollar) is a RESTRICTED stable: issued only on ethereum/solana/robinhood
  // (the USDGLO guard — NOT on base/op/arb/polygon/bnb). Unlike USDC/USDT (all-chain), it carries an
  // explicit availableOn so it never offers a chain it doesn't exist on.
  { symbol: 'USDG', name: 'Global Dollar', isNative: false, color: '#C7E36C', availableOn: ['ethereum', 'solana', 'robinhood'] },
  { symbol: 'BNB',  name: 'BNB',      isNative: true, color: '#F0B90B', availableOn: ['bnb'] },
  // #174 — Robinhood (4663) native gas is ETH, so RH lives under the ETH token (no dedicated RH button).
  { symbol: 'ETH',  name: 'Ethereum', isNative: true, color: '#627EEA', availableOn: ['ethereum', 'base', 'arbitrum', 'optimism', 'robinhood'] },
  { symbol: 'POL',  name: 'Polygon',  isNative: true, color: '#8247E5', availableOn: ['polygon'] },
  { symbol: 'SOL',  name: 'Solana',   isNative: true, color: '#9945FF', availableOn: ['solana'] },
  { symbol: 'TRX',  name: 'Tron',     isNative: true, color: '#FF0013', availableOn: ['tron'] },
]

/** Stablecoins as picker rows (network-agnostic — chain chosen separately). */
export const STABLECOIN_PICKER_LIST: TokenPickerItem[] = TOKEN_PICKER_LIST.filter(t => !t.isNative)

/**
 * #254 — NETWORK-FIRST native list. One selectable row per native-capable network, so
 * ETH-on-Base/Arbitrum/Optimism and Robinhood are directly pickable instead of hiding
 * behind a single generic "ETH" entry. Selecting a row sets the network's native token
 * AND that exact chain. Presentation is network-first (row = network name); `symbol`
 * stays the canonical token used for routing/balances/quotes — the label is view-only.
 *
 * Order is Josh's product rank (GATE 1, 2026-08-02), NOT alphabetical: it deliberately
 * front-loads the priority networks. Change the array to re-rank — order here IS the
 * render order.
 */
export interface NativeNetworkOption {
  chain: Chain
  /** Canonical native symbol (ETH/SOL/POL/BNB/TRX) — the routing/data identity. */
  symbol: string
  /** Display label (network-first), e.g. "Base", "Robinhood". */
  networkName: string
}

const NATIVE_NETWORK_ORDER: Chain[] = [
  'ethereum', 'base', 'solana', 'robinhood', 'bnb', 'polygon', 'tron', 'arbitrum', 'optimism',
]

/**
 * I-3, the TOKEN-picker half. `NATIVE_NETWORK_ORDER` hand-lists 'robinhood' and
 * `TokenSelectorModal` renders every entry unconditionally, writing the row's `chain` straight into
 * `sourceChain`. Filtering only the derived `*_CHAINS` lists would close the CHAIN picker and leave
 * the TOKEN picker still handing the user a Robinhood row on a testnet build — where the swap path
 * cannot resolve a chain id. Same predicate as `DEX_CHAINS`, so the two pickers cannot disagree.
 */
export const NATIVE_NETWORK_OPTIONS: NativeNetworkOption[] = NATIVE_NETWORK_ORDER
  .filter(isChainOnThisBuild)
  .map(chain => ({
    chain,
    symbol: CHAIN_REGISTRY[chain].nativeCurrency,
    networkName: CHAIN_REGISTRY[chain].name,
  }))
